import { computed, onBeforeUnmount, ref } from "vue";
import { Ioc } from "@core/ioc/ioc";
import type { IProjectModel } from "@models/project/IProjectModel";
import { PROJECT_MODEL } from "@models/project/ProjectModel";
import type { IProjectListModel } from "@models/project/IProjectListModel";
import { PROJECT_LIST_MODEL } from "@models/project/IProjectListModel";
import { formatTransferProgress } from "@models/utils/byteSize";

/**
 * `useLoadProjectProgress` — composable backing
 * `LoadProjectProgressView`.
 *
 * Mirrors Unity's `LoadProjectProgressViewModel`: holds the active
 * project's display name, listens to {@link IProjectModel.onLoadProjectProgress},
 * and tracks the begin / completed / failed lifecycle so the overlay
 * can show / hide automatically.
 *
 * Cancel calls `IProjectModel.cancelLoadProject()` which is the same
 * method `OnCancel` triggers in the Unity VM.
 */
export function useLoadProjectProgress() {
  const projectModel = Ioc.resolve<IProjectModel>(PROJECT_MODEL);
  const listModel = Ioc.resolve<IProjectListModel>(PROJECT_LIST_MODEL);

  const isVisible = ref<boolean>(false);
  const projectId = ref<string | null>(null);
  const projectName = ref<string>("New Project");
  const percentage = ref<number>(0);
  /**
   * `"5.5 of 492 MB"` for the scene's asset downloads — the last quarter of
   * this overlay, where the minutes actually go. Empty for the whole metadata
   * phase before it, which is what keeps the label from appearing with
   * nothing behind it.
   */
  const transferText = ref<string>("");
  const isCancelling = ref<boolean>(false);
  const error = ref<Error | null>(null);

  function setProjectName(id: string): void {
    const info = listModel.getById(id);
    projectName.value = info?.name ?? "New Project";
  }

  const offBegin = projectModel.onBeginLoadProject.on(({ projectId: id }) => {
    projectId.value = id;
    setProjectName(id);
    percentage.value = 0;
    transferText.value = "";
    isCancelling.value = false;
    error.value = null;
    isVisible.value = true;
  });

  const offProgress = projectModel.onLoadProjectProgress.on(
    ({ projectId: id, progress01, bytes }) => {
      if (projectId.value !== id) return;
      percentage.value = clamp(progress01 * 100);
      transferText.value = bytes ? formatTransferProgress(bytes.loaded, bytes.total) : "";
    },
  );

  const offCompleted = projectModel.onLoadProjectCompleted.on(({ projectId: id }) => {
    if (projectId.value !== id) return;
    percentage.value = 100;
    // Brief settle so the user sees the bar fill before it disappears.
    window.setTimeout(() => {
      if (projectId.value === id) {
        isVisible.value = false;
        projectId.value = null;
      }
    }, 200);
  });

  const offFailed = projectModel.onLoadProjectFailed.on(({ projectId: id, error: err }) => {
    if (projectId.value !== id) return;
    error.value = err;
    isVisible.value = false;
    projectId.value = null;
  });

  onBeforeUnmount(() => {
    offBegin();
    offProgress();
    offCompleted();
    offFailed();
  });

  function cancel(): void {
    if (!projectId.value || isCancelling.value) return;
    isCancelling.value = true;
    projectModel.cancelLoadProject();
  }

  return {
    isVisible,
    projectName: computed(() => projectName.value),
    percentage,
    transferText: computed(() => transferText.value),
    isCancelling,
    canCancel: computed(() => !isCancelling.value),
    error,
    cancel,
  };
}

function clamp(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 100) return 100;
  return v;
}
